import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressRolesManagementApiRolesManagementServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/contributors/accept',
        destPath: '/contributors/accept',
      },
      {
        srcPath: '/roles-management-web',
        destPath: '',
      },
      {
        srcPath: '/team/accept',
        destPath: '/team/accept',
      },
      {
        srcPath: '/_api/roles-management-web',
        destPath: '',
      },
      {
        srcPath: '/app/accept',
        destPath: '/app/accept',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/roles-management-web',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/roles-management-web',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/roles-management-web',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/roles-management-web',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/roles-management-web',
        destPath: '',
      },
      {
        srcPath: '/roles-management-web',
        destPath: '',
      },
      {
        srcPath: '/contributors/accept',
        destPath: '/contributors/accept',
      },
      {
        srcPath: '/team/accept',
        destPath: '/team/accept',
      },
    ],
    'dev._base_domain_': [
      {
        srcPath: '/_api/roles-management-web',
        destPath: '',
      },
    ],
    'platform.rise.ai': [
      {
        srcPath: '/v2/contributors',
        destPath: '/v2/contributors',
      },
      {
        srcPath: '/contributor/change/role',
        destPath: '/contributor/change/role',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/roles-management',
        destPath: '',
      },
      {
        srcPath: '/roles-management-web',
        destPath: '',
      },
      {
        srcPath: '/roles-management/roles',
        destPath: '/roles',
      },
      {
        srcPath: '/roles-management/team',
        destPath: '/team',
      },
      {
        srcPath: '/roles-management/v3/team',
        destPath: '/v3/team',
      },
      {
        srcPath: '/roles-management/v2/team',
        destPath: '/v2/team',
      },
      {
        srcPath: '/roles-management/contributor/change',
        destPath: '/contributor/change',
      },
      {
        srcPath: '/roles-management/v2/contributors',
        destPath: '/v2/contributors',
      },
      {
        srcPath: '/roles-management/contributor/remove',
        destPath: '/contributor/remove',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/identity/contributor',
        destPath: '/contributor',
      },
    ],
    'vibe._base_domain_': [
      {
        srcPath: '/roles-management-web/v2/contributors',
        destPath: '/v2/contributors',
      },
      {
        srcPath: '/roles-management-web/myRoles',
        destPath: '/roles-management-web/myRoles',
      },
    ],
    _: [
      {
        srcPath: '/identity/contributor',
        destPath: '/contributor',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/_api/roles-management-web',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/roles-management-web',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/roles-management-web',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_user-management_users';

/**
 * Removes a team member from the requesting account.
 * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
 */
export function removeMember(payload: object): RequestOptionsFactory<any> {
  function __removeMember({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.identity.roles.v1.user',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.roles.management.api.RolesManagementService.RemoveMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRolesManagementApiRolesManagementServiceUrl({
        protoPath: '/team/remove',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __removeMember;
}

/**
 * Retrieves a list of all team members of the requesting account with the specified filtering, sorting, and paging.
 *
 * > **Important**: This call requires an account level API key and can't be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
 *
 * ## Filters
 *
 * When using filters for dates, you must use
 * [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
 *
 * ## See also
 *
 * To learn about working with *Search* methods, see
 * [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) and
 * [Sorting and Paging](https://dev.wix.com/docs/rest/articles/get-started/sorting-and-paging).
 */
export function searchTeam(payload: object): RequestOptionsFactory<any> {
  function __searchTeam({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.identity.roles.v1.user',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.roles.management.api.RolesManagementService.SearchTeam',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRolesManagementApiRolesManagementServiceUrl({
        protoPath: '/v3/team/search',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'teamMembers.user.joinedTeamAt' },
              { path: 'teamMembers.invite.dateCreated' },
              { path: 'teamMembers.invite.dateUpdated' },
              { path: 'teamMembers.invite.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __searchTeam;
}

/**
 * Retrieves all available roles in the requesting account, including predefined and custom roles.
 * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
 */
export function getRolesInfo(payload: object): RequestOptionsFactory<any> {
  function __getRolesInfo({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.identity.roles.v1.user',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.roles.management.api.RolesManagementService.GetRolesInfo',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRolesManagementApiRolesManagementServiceUrl({
        protoPath: '/roles',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getRolesInfo;
}

/**
 * Updates the roles and conditions for an existing team member. Changing roles changes the team member's access to account assets.
 * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
 */
export function changeRoleV2(payload: object): RequestOptionsFactory<any> {
  function __changeRoleV2({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.identity.roles.v1.user',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.roles.management.api.RolesManagementService.ChangeRoleV2',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRolesManagementApiRolesManagementServiceUrl({
        protoPath: '/v2/team/roles',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __changeRoleV2;
}

/**
 * Updates the assignments of roles and conditions for an existing team member.  Changing assignments changes the team member’s access to account assets.
 * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
 */
export function updateTeamMemberAssignments(
  payload: object
): RequestOptionsFactory<any> {
  function __updateTeamMemberAssignments({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.identity.roles.v1.user',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.roles.management.api.RolesManagementService.UpdateTeamMemberAssignments',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRolesManagementApiRolesManagementServiceUrl({
        protoPath: '/v2/team/assignments',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __updateTeamMemberAssignments;
}
